import React, {MouseEvent, useCallback, useRef, useState} from 'react';
import cx from 'classnames';
import {Icon, PriorityIcon, Text} from '@priority-software/priority-style-react';
import {priority} from '@priority-software/priority-proto-api/lib';
import {isValid} from '@priority-software/priority-utils/lib/date';
import {FunctionIcon} from '../FunctionIcon/FunctionIcon';
import {DatePickerExtended} from '../DatePickerExtended/DatePickerExtended';
import {ruleDialogID} from '../../../../../../constants';
import {mapDateZoomToColumnType} from '../../utils';
import styles from './DateControl.module.scss';
import {getStringFormatByColumnType} from '../../../../../../../Form/utils/date';
import {getLang} from '../../../../../../../../Localization';
import {texts} from '../../../../../../texts';
import {OldZoomValues} from '../../../../enum';
import {fetchDateChooses} from './utils';

type Props = {
    openFunctionDialog: (e: MouseEvent<HTMLElement>) => void;
    session: string;
    value: string;
    columnID: number;
    onChange: (value: string, name: string) => void;
    name: string;
    onCriticalError: () => void;
    zoom?: priority.netitems.form.ZoomType | OldZoomValues;
    placeholder?: string;
};

export const DateControl = (props: Props) => {
    const {
        openFunctionDialog,
        session,
        columnID,
        value = '',
        onChange,
        name,
        zoom,
        placeholder,
        onCriticalError
    } = props;
    const [isOpen, setIsOpen] = useState(false);

    const [boundingRect, setBoundingRect] = useState(null);
    const [options, setOptions] = useState([]);

    const anchorRef = useRef<HTMLDivElement>(null);
    const hasOptions = options.length > 0;

    const openDatePicker = useCallback(async () => {
        if (!hasOptions) {
            const isOldAPI = zoom === OldZoomValues.date;
            const id = isOldAPI ? -1 : columnID;
            const options = await fetchDateChooses(session, id, isOldAPI, onCriticalError);

            setOptions(options);
        }

        setIsOpen(true);
        setBoundingRect(anchorRef.current?.getBoundingClientRect());
    }, [session, columnID, hasOptions, onCriticalError]);

    const getAnchor = useCallback(() => anchorRef.current, []);

    const onDateChange = useCallback(
        (val: string) => {
            onChange(val, name);
        },
        [onChange, name]
    );

    const columnType = mapDateZoomToColumnType(zoom);
    const format = getStringFormatByColumnType(columnType, getLang());

    const isDateValid = isValid(value, format);

    return (
        <>
            <div
                className={cx(styles.component, 'react-onclickoutside-ignore')}
                ref={anchorRef}
                onClick={isOpen ? () => setIsOpen(false) : openDatePicker}
            >
                <div
                    className={cx(
                        styles.inputWrapper,
                        styles.box,
                        styles.medium,
                        styles.inputWrapperPaddingNone
                    )}
                >
                    <Icon icon={PriorityIcon.calendar} componentClasses={styles.calendarIcon} />

                    <div
                        className={styles.input}
                        title={
                            value
                                ? `${value} ${isDateValid ? texts.date_control_custom_label : ''}`
                                : undefined
                        }
                    >
                        {value ? (
                            <Text
                                componentClasses={styles.inputTextValue}
                                tag="span"
                                skin="paragraph-2"
                                textColor="primary-700"
                            >
                                {value}
                                {isDateValid ? (
                                    <span className={styles.valueLabel}>
                                        {' '}
                                        | {texts.date_control_custom_label}
                                    </span>
                                ) : null}
                            </Text>
                        ) : (
                            <Text tag="span" skin="text-placeholder-2" textColor="secondary-400">
                                {placeholder}
                            </Text>
                        )}
                    </div>

                    <FunctionIcon openFunctionDialog={openFunctionDialog} />
                </div>
            </div>

            <DatePickerExtended
                value={value}
                setIsOpen={setIsOpen}
                portalEl={document.getElementById(ruleDialogID)}
                isOpen={isOpen}
                getAnchor={getAnchor}
                columnType={columnType}
                onChange={onDateChange}
                boundingRect={boundingRect}
                options={options}
                hasCustomDate={isDateValid}
                shiftPositionY={5}
            />
        </>
    );
};
